import { AttributeOptions, FNOL_RequestForm } from '@dispatch/shared/types';
import { SubSection } from '@dispatch/web/ui';
import {
  Control,
  FieldValues,
  useFieldArray,
  UseFormGetValues,
  UseFormWatch,
} from 'react-hook-form';
import { FNOLClaimAttributesAdd } from './FNOLClaimAttributesAdd';
import { Divider } from '@mui/material';
import { FNOLClaimAttributesForm } from './FNOLClaimAttributesForm';
import type { IndexedField } from '../pinnedClaimAttributes';
import { partitionPinnedFields } from '../pinnedClaimAttributes';

export type FNOLClaimAttributesSectionProps = {
  control: Control<FieldValues>;
  watch: UseFormWatch<FNOL_RequestForm>;
  getValues: UseFormGetValues<FNOL_RequestForm>;
  companyAttributes: AttributeOptions[];
  /** undefined while the tenant's pinned attributes are still resolving */
  pinnedAttributeGuids: readonly string[] | undefined;
};

export function FNOLClaimAttributesSection({
  control,
  watch,
  getValues,
  companyAttributes,
  pinnedAttributeGuids,
}: FNOLClaimAttributesSectionProps) {
  const { fields, remove, append } = useFieldArray({
    control,
    name: 'claimAttributes',
  });

  const { pinned, others } = partitionPinnedFields(fields, (index) =>
    (pinnedAttributeGuids ?? []).includes(
      getValues(`claimAttributes.${index}.attributeDefinitionIntegrationGUID`)
    )
  );

  const renderRow =
    (isPinned: boolean) =>
    ({ field, index }: IndexedField<Record<'id', string>>) =>
      (
        <FNOLClaimAttributesForm
          key={field.id}
          control={control}
          claimAttributeIndex={index}
          claimAttributeRemove={remove}
          field={field}
          getValues={getValues}
          companyAttributes={companyAttributes}
          isPinned={isPinned}
        />
      );

  return (
    <div
      style={{
        padding: '24px',
      }}
    >
      <SubSection title={'Claim Attributes'}>
        {pinned.map(renderRow(true))}
        <>
          <FNOLClaimAttributesAdd
            append={append}
            control={control}
            watch={watch}
            companyAttributes={companyAttributes}
            pinnedAttributeGuids={pinnedAttributeGuids}
          />
          <Divider />
        </>

        {others.map(renderRow(false))}
      </SubSection>
    </div>
  );
}
